import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useNotification } from '../../hooks/useNotification';
import { useReservation } from '../../hooks/useReservation';
import { validationService, SALON_HOURS } from '../../services/validationService';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import Alert from '../../components/Common/Alert';
import SEO from '../../components/SEO/SEO';
import {
    FaCalendarAlt,
    FaClock,
    FaDollarSign,
    FaFileAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaExclamationCircle,
    FaPlus,
    FaEye,
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaTrash,
    FaInbox
} from 'react-icons/fa';
import styles from './styles/ReservationPage.module.css';

const ITEMS_PER_PAGE = 10;

export default function ReservationPage() {
    const { user } = useContext(AuthContext);
    const { showNotification } = useNotification();
    const {
        reservations,
        loading,
        createReservation,
        fetchUserReservations,
        cancelReservation,
    } = useReservation();

    const [showForm, setShowForm] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [availableHours, setAvailableHours] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [formData, setFormData] = useState({
        service: 'coupe',
        date: '',
        time: '',
        notes: '',
    });

    const services = [
        { id: 'coupe', name: 'Coupe de cheveux', price: '25€' },
        { id: 'coloration', name: 'Coloration', price: '50€' },
        { id: 'lissage', name: 'Lissage brésilien', price: '80€' },
        { id: 'extension', name: 'Extensions', price: '100€' },
    ];

    // Charger les réservations au montage
    useEffect(() => {
        if (user?.uid) {
            fetchUserReservations(user.uid);
        }
    }, [user?.uid, fetchUserReservations]);

    // Réinitialiser la page quand les réservations changent
    useEffect(() => {
        setCurrentPage(1);
    }, [reservations]);

    // Mettre à jour les heures disponibles quand la date ou le service change
    useEffect(() => {
        if (formData.date) {
            const hours = validationService.getAvailableHours(
                formData.date,
                formData.service,
                reservations
            );
            setAvailableHours(hours);
            setFormData((prev) => ({ ...prev, time: '' }));
        }
    }, [formData.date, formData.service, reservations]);

    // Bloquer le scroll quand le modal est ouvert
    useEffect(() => {
        if (showForm) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showForm]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setFormErrors([]);
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getDayStatus = (dateString) => {
        const date = new Date(dateString);
        const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' }).toLowerCase();
        const dayHours = SALON_HOURS[dayName];

        if (!dayHours || dayHours.closed) {
            return { text: 'Fermé', icon: FaTimesCircle, color: 'danger' };
        }

        return { text: `${dayHours.open} - ${dayHours.close}`, icon: FaCheckCircle, color: 'success' };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormErrors([]);

        // Validation
        if (!formData.date || !formData.time) {
            setFormErrors(['Veuillez remplir tous les champs obligatoires']);
            return;
        }

        try {

            // Valider la réservation
            const validation = await validationService.validateReservation(
                formData.date,
                formData.time,
                formData.service,
                reservations
            );

            if (!validation.isValid) {

                setFormErrors(validation.errors);
                validation.errors.forEach((error) => {
                    showNotification(error, 'error');
                });
                return;
            }

            await createReservation(user.uid, {
                service: formData.service,
                date: formData.date,
                time: formData.time,
                notes: formData.notes,
            });

            showNotification('Réservation confirmée!', 'success');

            // Réinitialiser le formulaire
            setFormData({
                service: 'coupe',
                date: '',
                time: '',
                notes: '',
            });

            setShowForm(false);
        } catch (error) {
            console.error('Erreur:', error);
            showNotification(`${error.message}`, 'error');
        }
    };

    const handleCancel = async (reservationId) => {
        if (window.confirm('Êtes-vous sûr de vouloir annuler cette réservation?')) {
            try {
                await cancelReservation(reservationId);
                showNotification('Réservation annulée', 'success');
            } catch (error) {
                showNotification(`${error.message}`, 'error');
            }
        }
    };

    const getServiceName = (serviceId) => {
        return services.find((s) => s.id === serviceId)?.name || serviceId;
    };

    const getServicePrice = (serviceId) => {
        return services.find((s) => s.id === serviceId)?.price || '?';
    };

    // Pagination
    const totalPages = Math.ceil(reservations.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedReservations = reservations.slice(startIndex, endIndex);

    // Statistiques
    const stats = {
        total: reservations.length,
        pending: reservations.filter(r => r.status === 'pending').length,
        confirmed: reservations.filter(r => r.status === 'confirmée').length,
        cancelled: reservations.filter(r => r.status === 'annulée').length,
    };

    return (
        <>
            <SEO
                title="Réserver un Rendez-vous | OV Hairstyle"
                description="Réservez facilement votre rendez-vous chez OV Hairstyle. Choisissez votre service, date et heure préférés."
                url="https://ov-hairstyle.vercel.app/reservation"
                image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
            />
            <Layout>
                <div className={styles.container}>
                    {/* Header */}
                    <div className={styles.header}>
                        <h1>Mes Réservations</h1>
                        <Button onClick={() => setShowForm(!showForm)}>
                            {showForm ? (
                                <>
                                    <FaTimes /> Annuler
                                </>
                            ) : (
                                <>
                                    <FaPlus /> Nouvelle réservation
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Dashboard Stats */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#d4af37' }}>
                                <FaCalendarAlt />
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statNumber}>{stats.total}</div>
                                <div className={styles.statLabel}>Total</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#ffc107' }}>
                                <FaExclamationCircle />
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statNumber}>{stats.pending}</div>
                                <div className={styles.statLabel}>En attente</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#28a745' }}>
                                <FaCheckCircle />
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statNumber}>{stats.confirmed}</div>
                                <div className={styles.statLabel}>Confirmées</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#dc3545' }}>
                                <FaTimesCircle />
                            </div>
                            <div className={styles.statContent}>
                                <div className={styles.statNumber}>{stats.cancelled}</div>
                                <div className={styles.statLabel}>Annulées</div>
                            </div>
                        </div>
                    </div>

                    {/* Modal Overlay */}
                    {showForm && (
                        <>
                            {/* Backdrop */}
                            <div
                                className={styles.backdrop}
                                onClick={() => setShowForm(false)}
                            />

                            {/* Modal Form */}
                            <div className={styles.modalContainer}>
                                <div className={styles.modalContent}>
                                    {/* Modal Header */}
                                    <div className={styles.modalHeader}>
                                        <h2>Créer une réservation</h2>
                                        <button
                                            className={styles.closeBtn}
                                            onClick={() => setShowForm(false)}
                                            aria-label="Fermer"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>

                                    {/* Modal Body */}
                                    <div className={styles.modalBody}>
                                        {formErrors.length > 0 && (
                                            <div className={styles.errors}>
                                                {formErrors.map((error, idx) => (
                                                    <Alert
                                                        key={idx}
                                                        type="error"
                                                        message={error}
                                                        onClose={() =>
                                                            setFormErrors((prev) => prev.filter((_, i) => i !== idx))
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit} className={styles.form}>
                                            <div className={styles.formGroup}>
                                                <label>Service *</label>
                                                <select
                                                    name="service"
                                                    value={formData.service}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    {services.map((s) => (
                                                        <option key={s.id} value={s.id}>
                                                            {s.name} - {s.price}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Date *</label>
                                                <input
                                                    type="date"
                                                    name="date"
                                                    value={formData.date}
                                                    onChange={handleChange}
                                                    min={getMinDate()}
                                                    required
                                                />
                                                {formData.date && (
                                                    <small className={styles.dayStatus}>
                                                        {React.createElement(getDayStatus(formData.date).icon, {
                                                            style: {
                                                                marginRight: '6px',
                                                                color: getDayStatus(formData.date).color === 'danger' ? '#f44336' : '#4caf50'
                                                            }
                                                        })}
                                                        {getDayStatus(formData.date).text}
                                                    </small>
                                                )}
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Heure *</label>
                                                {formData.date ? (
                                                    <>
                                                        {availableHours.length > 0 ? (
                                                            <select
                                                                name="time"
                                                                value={formData.time}
                                                                onChange={handleChange}
                                                                required
                                                            >
                                                                <option value="">-- Sélectionnez une heure --</option>
                                                                {availableHours.map((hour) => (
                                                                    <option key={hour} value={hour}>
                                                                        {hour}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <div className={styles.noHours}>
                                                                <FaTimesCircle style={{ marginRight: '8px' }} />
                                                                Aucune heure disponible ce jour
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <input
                                                        type="time"
                                                        name="time"
                                                        value={formData.time}
                                                        onChange={handleChange}
                                                        disabled
                                                        placeholder="Sélectionnez d'abord une date"
                                                    />
                                                )}
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Notes (optionnel)</label>
                                                <textarea
                                                    name="notes"
                                                    value={formData.notes}
                                                    onChange={handleChange}
                                                    placeholder="Vos demandes spéciales..."
                                                    rows="4"
                                                />
                                            </div>

                                            <div className={styles.modalActions}>
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => setShowForm(false)}
                                                >
                                                    <FaTimes /> Annuler
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    loading={loading}
                                                    disabled={!formData.date || !formData.time || loading}
                                                >
                                                    <FaCheckCircle /> Confirmer
                                                </Button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Liste des réservations */}
                    <div className={styles.reservationsList}>
                        {loading && <p className={styles.loading}>Chargement...</p>}

                        {reservations.length === 0 && !loading && (
                            <div className={styles.empty}>
                                <FaInbox style={{ fontSize: '48px', marginBottom: '15px', color: '#d4af37' }} />
                                <p>Aucune réservation pour le moment</p>
                                <Button onClick={() => setShowForm(true)}>
                                    <FaPlus /> Créer une réservation
                                </Button>
                            </div>
                        )}

                        {paginatedReservations.map((reservation) => (
                            <div key={reservation.id} className={styles.reservationCard}>
                                <div className={styles.reservationHeader}>
                                    <h3>{getServiceName(reservation.service)}</h3>
                                    <span className={`${styles.status} ${styles[reservation.status]}`}>
                                        {reservation.status === 'pending' && (
                                            <>
                                                <FaExclamationCircle /> En attente
                                            </>
                                        )}
                                        {reservation.status === 'confirmée' && (
                                            <>
                                                <FaCheckCircle /> Confirmée
                                            </>
                                        )}
                                        {reservation.status === 'annulée' && (
                                            <>
                                                <FaTimesCircle /> Annulée
                                            </>
                                        )}
                                    </span>
                                </div>

                                <div className={styles.reservationDetails}>
                                    <div className={styles.detail}>
                                        <span className={styles.label}>
                                            <FaCalendarAlt /> Date:
                                        </span>
                                        <span>{new Date(reservation.date).toLocaleDateString('fr-FR')}</span>
                                    </div>

                                    <div className={styles.detail}>
                                        <span className={styles.label}>
                                            <FaClock /> Heure:
                                        </span>
                                        <span>{reservation.time}</span>
                                    </div>

                                    <div className={styles.detail}>
                                        <span className={styles.label}>
                                            <FaDollarSign /> Prix:
                                        </span>
                                        <span>{getServicePrice(reservation.service)}</span>
                                    </div>

                                    {reservation.notes && (
                                        <div className={styles.detail}>
                                            <span className={styles.label}>
                                                <FaFileAlt /> Notes:
                                            </span>
                                            <span>{reservation.notes}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className={styles.actions}>
                                    <Link to={`/reservation/${reservation.id}`}>
                                        <Button variant="primary">
                                            <FaEye /> Voir détails
                                        </Button>
                                    </Link>

                                    {reservation.status === 'confirmée' && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleCancel(reservation.id)}
                                            loading={loading}
                                        >
                                            <FaTrash /> Annuler
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                className={styles.paginationBtn}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                            >
                                <FaChevronLeft /> Précédent
                            </button>

                            <div className={styles.pageNumbers}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>

                            <button
                                className={styles.paginationBtn}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Suivant <FaChevronRight />
                            </button>

                            <div className={styles.paginationInfo}>
                                Page {currentPage} sur {totalPages}
                            </div>
                        </div>
                    )}
                </div>
            </Layout>
        </>
    );
}
